// The client side of /api/network (BUILD-BRIEF-R5 §6.1, §6.5) — the organisation links, the
// graph and the payer table.
//
// A 404/501 leaves the caller silent, never zero (§6.5): `NetworkZone` and `NetworkPage` catch
// the rejection themselves and render "not collected" / an empty state, never a manufactured
// count. Nothing here invents a fallback value.
import { qs, request } from './client.js';
import type {
  FlowsResponse, LinkQualifier, LinkRow, NetworkResponse, OrganisationLinks, PayerRow,
} from '../../shared/network.v1.js';

export interface NetworkFilters {
  role?: string;
  canton?: string;
  regime?: string;
  subsector?: string;
  year?: number;
  held_only?: boolean;
  group?: 'regime';
  q?: string;
  focus?: number;
}

export interface PayerFilters {
  year?: number;
  regime?: string;
  canton?: string;
  group?: 'regime';
}

/** The links FROM and TO one organisation — the Network zone's whole payload (§6.1, §6.2). */
export function getOrganisationLinks(organisationId: number): Promise<OrganisationLinks> {
  return request(`/network/organisations/${organisationId}/links`);
}

/** The graph: nodes, edges, and the summary the Network page's rail reads (§6.1, §6.3). */
export function getNetwork(filters: NetworkFilters = {}): Promise<NetworkResponse> {
  return request(`/network${qs({ ...filters, held_only: filters.held_only ? '1' : undefined })}`);
}

/** The Payers zone's rows — one per counterpart organisation, or one per regime with `group`. */
export function getPayers(filters: PayerFilters = {}): Promise<{ items: PayerRow[]; total: number }> {
  return request(`/network/payers${qs({ ...filters })}`);
}

export type FlowFilters = Omit<NetworkFilters, 'focus' | 'q'>;

/** The redesign's one payload: payers with coverage, providers with their mix, the matrix cells,
 *  the facet counts and the rail's summary (docs/R5-NETWORK-DESIGN.md). */
export function getFlows(filters: FlowFilters = {}): Promise<FlowsResponse> {
  return request(`/network/flows${qs({ ...filters, held_only: filters.held_only ? '1' : undefined })}`);
}

export interface DeclareLinkInput {
  subject_id: number;
  counterpart_id?: number;
  regime?: string;
  residual?: string;
  role: string;
  basis?: string;
  quantity?: number | string;
  qualifier?: LinkQualifier;
  year?: number;
  label?: string;
  note?: string;
}

/** The operator's own declared link — `method: 'declared'`, held from the moment it is written
 *  (§6.1). Never how an agent files one: that is `propose_link`, held by rule or not at all. */
export function declareLink(input: DeclareLinkInput): Promise<LinkRow> {
  return request('/network/links', { method: 'POST', body: JSON.stringify(input) });
}

export interface SupersedeLinkInput {
  quantity: number | string;
  qualifier?: LinkQualifier;
  note: string;
}

export function supersedeLink(id: number, input: SupersedeLinkInput): Promise<LinkRow> {
  return request(`/network/links/${id}/supersede`, { method: 'POST', body: JSON.stringify(input) });
}

export function endLink(id: number, endedOn: string): Promise<LinkRow> {
  return request(`/network/links/${id}/end`, { method: 'POST', body: JSON.stringify({ ended_on: endedOn }) });
}

export function retractLink(id: number, note: string): Promise<LinkRow> {
  return request(`/network/links/${id}/retract`, { method: 'POST', body: JSON.stringify({ retract_note: note }) });
}
